import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import ChapterDepth from "../components/ChapterDepth";
import ChapterEchoes from "../components/ChapterEchoes";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, BookOpen, Search, FileText, ChevronLeft, CheckCircle2, Circle, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SaveButton from "../components/SaveButton";


export default function Library() {
  const { slug } = useParams();
  if (slug) return <LibraryArticle slug={slug} />;
  return <LibraryIndex />;
}


function LibraryIndex() {
  const { user } = useAuth();
  const [data, setData] = useState({ articles: [], categories: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "120");
    api.get(`/library?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ articles: [], categories: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [category, q]);

  useEffect(() => {
    if (!user) return;
    api.get("/library/progress/me").then(({ data }) => setProgress(data)).catch(() => {});
  }, [user]);

  const readSet = useMemo(() => new Set(progress?.read_slugs || []), [progress]);
  const perBookProgress = useMemo(() => {
    const map = {};
    (progress?.per_book || []).forEach((b) => { map[b.book_code] = b; });
    return map;
  }, [progress]);

  // Group articles by category → subcategory for the outline
  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of data.articles || []) {
      const key = a.category || "uncategorised";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return Array.from(map.entries());
  }, [data.articles]);

  // Look up the human title for a category chip / group header
  const categoryMeta = useMemo(() => {
    const map = {};
    (data.categories || []).forEach((c) => { map[c.category] = c; });
    return map;
  }, [data.categories]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-24" data-testid="library-page">
      <div className="mb-10 max-w-3xl">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><BookOpen className="w-3 h-3" /> The Curriculum</div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-[1.02] mb-4">
          Extra reading, <span className="italic text-marigold">imported from the field</span>
        </h1>
        <p className="text-lg text-ink2 leading-relaxed">
          A growing pile of Markdown-first lessons — imported by the team and preserved verbatim.  Skim by book, search across titles and tags, and open any chapter for the full reader.
        </p>
        {user && progress && progress.read_count > 0 && (
          <div className="mt-4 text-xs font-mono text-marigold" data-testid="library-progress-summary">
            You've finished <span className="font-bold">{progress.read_count}</span> of {data.total} chapters.
          </div>
        )}
      </div>

      {/* Search */}
      <div className="mb-6 flex items-center gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-ink2 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, summaries, tags…"
            className="w-full pl-9 pr-4 py-2 rounded-full border-2 border-ink bg-cream text-sm"
            data-testid="library-search"
          />
        </div>
        {q && <button onClick={() => setQ("")} className="text-[10px] uppercase tracking-widest font-bold text-ink2 hover:text-ink" data-testid="library-search-clear">Clear</button>}
      </div>

      {/* Category chips — now with book titles + progress dots */}
      {data.categories.length > 0 && (
        <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="library-category-filter">
          <button
            onClick={() => setCategory(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium border-2 transition ${category === null ? "border-ink bg-ink text-cream" : "border-ink/20 text-ink2 hover:border-ink"}`}
            data-testid="library-category-all"
          >
            All · {data.total}
          </button>
          {data.categories.map((c) => {
            const bp = perBookProgress[c.book_code];
            const pct = bp?.pct || 0;
            return (
              <button
                key={c.category}
                onClick={() => setCategory(c.category)}
                className={`group relative px-3 py-1 rounded-full text-xs font-medium border-2 transition overflow-hidden ${category === c.category ? "border-marigold bg-marigold/20 text-marigold" : "border-ink/20 text-ink2 hover:border-ink"}`}
                data-testid={`library-category-${c.category}`}
                title={c.blurb || ""}
              >
                {pct > 0 && (
                  <span className="absolute inset-y-0 left-0 bg-marigold/15 pointer-events-none" style={{ width: `${pct}%` }} aria-hidden="true" />
                )}
                <span className="relative">
                  {c.book_title || c.category}
                  <span className="opacity-60 ml-1">· {c.count}</span>
                  {pct > 0 && <span className="ml-1 font-mono text-[10px] text-marigold" data-testid={`library-category-pct-${c.book_code}`}>{pct}%</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {loading && <div className="text-ink2 text-sm">Loading…</div>}

      {!loading && data.total === 0 && (
        <div className="card-soft p-8 text-center" data-testid="library-empty">
          <FileText className="w-8 h-8 text-marigold mx-auto mb-3" />
          <div className="font-heading text-2xl mb-2">Nothing here yet.</div>
          <p className="text-ink2">
            An admin can drop a zip of Markdown at <Link to="/admin" className="text-marigold hover:underline">/admin → Library</Link> to populate this shelf.
          </p>
        </div>
      )}

      {!loading && data.total > 0 && data.articles.length === 0 && (
        <div className="text-ink2 text-sm italic" data-testid="library-no-results">
          Nothing matches "{q}"{category ? ` in ${category}` : ""}.
        </div>
      )}

      {!loading && grouped.length > 0 && (
        <div className="space-y-10" data-testid="library-grouped">
          {grouped.map(([cat, items]) => {
            const meta = categoryMeta[cat] || {};
            const bp = perBookProgress[meta.book_code];
            return (
              <section key={cat} data-testid={`library-section-${cat}`}>
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl">{meta.book_title || cat}</h2>
                    {meta.blurb && <div className="text-xs text-ink2 italic mt-0.5">{meta.blurb}</div>}
                  </div>
                  <div className="text-[10px] font-mono text-ink2 flex items-center gap-2">
                    {bp && bp.read > 0 && (
                      <span className="text-marigold" data-testid={`library-section-progress-${meta.book_code}`}>
                        {bp.read}/{bp.total} read · {bp.pct}%
                      </span>
                    )}
                    <span>{items.length} chapter{items.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <ul className="grid md:grid-cols-2 gap-3">
                  {items.map((a) => {
                    const isRead = readSet.has(a.slug);
                    return (
                      <li key={a.id}>
                        <Link
                          to={`/library/${a.slug}`}
                          className={`block card-soft p-4 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#1F1A17] transition ${isRead ? "opacity-70" : ""}`}
                          data-testid={`library-card-${a.slug}`}
                        >
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <h3 className="font-heading text-lg leading-tight break-words flex items-start gap-1.5">
                              {isRead && <CheckCircle2 className="w-4 h-4 text-teal shrink-0 mt-0.5" data-testid={`library-card-read-${a.slug}`} />}
                              <Highlight text={a.title} q={q} />
                            </h3>
                            {a.subcategory && <span className="text-[10px] font-mono text-ink2 shrink-0">{a.subcategory}</span>}
                          </div>
                          {a.summary && (
                            <p className="text-sm text-ink2 mb-2 line-clamp-3">
                              <Highlight text={a.summary} q={q} />
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-ink2">
                            <span className="font-mono">{a.word_count} words</span>
                            {(a.tags || []).slice(0, 4).map((t) => (
                              <span key={t} className="px-1.5 py-0.5 rounded-full bg-marigold/10 text-marigold font-mono">{t}</span>
                            ))}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Highlights `q` matches (case-insensitive) inside a text span using <mark>.
function Highlight({ text, q }) {
  if (!text) return null;
  const query = (q || "").trim();
  if (!query) return <>{text}</>;
  try {
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = String(text).split(re);
    return (
      <>{parts.map((p, i) => (
        i % 2 === 1
          ? <mark key={i} className="bg-marigold/30 text-ink px-0.5 rounded-sm">{p}</mark>
          : <span key={i}>{p}</span>
      ))}</>
    );
  } catch {
    return <>{text}</>;
  }
}


function LibraryArticle({ slug }) {
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [markedRead, setMarkedRead] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setArticle(null); setRelated([]); setMarkedRead(false);
    api.get(`/library/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    api.get(`/library/${slug}/related`)
      .then(({ data }) => setRelated(data.related || []))
      .catch(() => setRelated([]));
  }, [slug]);

  // Auto-record a read as soon as the article body renders (signed-in users only).
  useEffect(() => {
    if (!user || !article || markedRead) return;
    api.post(`/library/${slug}/read`).then(() => setMarkedRead(true)).catch(() => {});
  }, [user, article, slug, markedRead]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 pt-28 pb-24 text-ink2 text-sm">Loading…</div>;
  }

  if (notFound || !article) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-24" data-testid="library-article-404">
        <Link to="/library" className="inline-flex items-center gap-1 text-xs text-ink2 hover:text-ink mb-4"><ChevronLeft className="w-3 h-3" /> Back to the library</Link>
        <h1 className="font-heading text-3xl mb-2">Article not found</h1>
        <p className="text-ink2">The link may be old or the article may have been removed.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pt-28 pb-24" data-testid="library-article">
      <Link to="/library" className="inline-flex items-center gap-1.5 text-ink2 hover:text-ink mb-6 text-sm" data-testid="library-article-back">
        <ArrowLeft className="w-4 h-4" /> Back to the library
      </Link>

      <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
        {article.book_title && <span data-testid="library-article-booktitle">{article.book_title}</span>}
        {article.book_title && article.subcategory && <span className="opacity-50">·</span>}
        {article.subcategory && <span>{article.subcategory}</span>}
      </div>
      <div className="flex items-start gap-3 mb-3">
        <h1 className="flex-1 font-heading text-4xl md:text-5xl font-semibold leading-[1.05]">{article.title}</h1>
        <SaveButton slug={article.slug} title={article.title} size="md" testId={`library-article-save-${article.slug}`} className="mt-2 flex-shrink-0" stopEvent={false} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink2 mb-8">
        {article.author && <span>By <span className="text-ink font-medium">{article.author}</span></span>}
        {article.author && <span>·</span>}
        <span className="font-mono">{article.word_count} words</span>
        {article.published_at && <><span>·</span><span className="font-mono">{article.published_at}</span></>}
        {user && markedRead && (
          <span className="inline-flex items-center gap-1 text-teal font-mono" data-testid="library-article-read-badge">
            <CheckCircle2 className="w-3 h-3" /> Read
          </span>
        )}
        {(article.tags || []).map((t) => (
          <span key={t} className="px-2 py-0.5 rounded-full bg-marigold/10 text-marigold font-mono">{t}</span>
        ))}
      </div>

      {article.body_md ? (
        <ChapterDepth slug={article.slug} learnBody={article.body_md} />
      ) : (
        <div className="card-soft p-6 text-ink2 text-sm">This article has no body content.</div>
      )}

      {/* "You've seen this idea before" whisper — silent when the learner
          is anonymous or when there's no cross-world overlap yet. */}
      <ChapterEchoes slug={article.slug} />

      {/* Related — previous / next / next-book */}
      {related.length > 0 && (
        <section className="mt-14 pt-8 border-t border-ink2/20" data-testid="library-article-related">
          <div className="label-eyebrow mb-4">Keep reading</div>
          <div className="grid md:grid-cols-3 gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/library/${r.slug}`}
                className="block card-soft p-4 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#1F1A17] transition"
                data-testid={`library-related-${r._relation}`}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-marigold mb-1 inline-flex items-center gap-1">
                  {r._relation === "previous" && <><ArrowLeft className="w-3 h-3" /> Previous</>}
                  {r._relation === "next" && <>Next <ArrowRight className="w-3 h-3" /></>}
                  {r._relation === "next-book" && <>Next book <ArrowUpRight className="w-3 h-3" /></>}
                </div>
                <div className="font-heading text-base leading-tight mb-1">{r.title}</div>
                {r.book_title && <div className="text-[10px] font-mono text-ink2">{r.book_title}</div>}
                {r.summary && <p className="text-xs text-ink2 mt-2 line-clamp-2">{r.summary}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 pt-6 border-t border-ink2/20 flex items-center justify-between text-sm">
        <Link to="/library" className="btn-outline-ink text-xs inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to the library</Link>
        <span className="text-[10px] font-mono text-ink2">source: {article.source_path}</span>
      </div>
    </div>
  );
}
